import { z } from 'zod'

/**
 * 알림 발송 이력·재발송 API 계약 (BIZ-54). 운영자 전용.
 *
 * 아웃박스(notification_log)는 도메인 워커가 주기적으로 드레이닝하지만, 운영자가
 * 발송 상태를 눈으로 확인하고 실패건을 수동 재발송할 수 있어야 한다(BIZ-89 승인 재발송).
 * 응답엔 개인정보·금액을 담지 않는다 — recipient(수신 번호)만, 그마저 마스킹해 내려준다.
 */

/** notification_log.status 4종. */
export const notificationStatus = z.enum(['PENDING', 'SENT', 'FAILED', 'FALLBACK_SENT'])
export type NotificationStatus = z.infer<typeof notificationStatus>

/** 발송 이력 한 줄. 공급가·고인명 등은 담지 않는다(payload엔 주문번호만). */
export const notificationLogView = z.object({
  id: z.number().int(),
  orderId: z.number().int().nullable(),
  orderNo: z.string().nullable(),
  /** 발송 채널. 대체 발송되면 SMS로 바뀐다. */
  channel: z.string(),
  templateCode: z.string(),
  /** 수신 번호(가운데 마스킹). 운영자 식별용. */
  recipientMasked: z.string(),
  status: notificationStatus,
  /** 지금까지 발송 시도 횟수. */
  attemptCount: z.number().int(),
  /** 다음 재시도 예정 시각(백오프). null이면 즉시 대상이거나 이미 종료 상태. */
  nextAttemptAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  failedReason: z.string().nullable(),
  createdAt: z.string(),
})
export type NotificationLogView = z.infer<typeof notificationLogView>

/** 목록 조회 조건. status 미지정이면 전체. */
export const notificationListQuery = z.object({
  status: notificationStatus.optional(),
  limit: z.coerce.number().int().positive().max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
})
export type NotificationListQuery = z.infer<typeof notificationListQuery>

/** 상태별 건수 요약(상단 카운터). */
export const notificationStatusCounts = z.object({
  PENDING: z.number().int(),
  SENT: z.number().int(),
  FAILED: z.number().int(),
  FALLBACK_SENT: z.number().int(),
})
export type NotificationStatusCounts = z.infer<typeof notificationStatusCounts>

export const notificationListResponse = z.object({
  items: z.array(notificationLogView),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  counts: notificationStatusCounts,
})
export type NotificationListResponse = z.infer<typeof notificationListResponse>

/** 아웃박스 드레이닝 결과(수동 트리거·재발송 후 처리 요약). */
export const processOutboxResponse = z.object({
  processed: z.number().int(),
  sent: z.number().int(),
  fallbackSent: z.number().int(),
  retried: z.number().int(),
  failed: z.number().int(),
})
export type ProcessOutboxResponse = z.infer<typeof processOutboxResponse>

/** 재발송 결과. 대상 건을 PENDING으로 되돌린 뒤(멱등키·번호는 그대로) 갱신된 행을 돌려준다. */
export const retryNotificationResponse = z.object({
  notification: notificationLogView,
})
export type RetryNotificationResponse = z.infer<typeof retryNotificationResponse>

export const NotificationErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  NOT_FOUND: 'NOT_FOUND',
  NOT_RETRIABLE: 'NOT_RETRIABLE',
} as const
export type NotificationErrorCode = (typeof NotificationErrorCode)[keyof typeof NotificationErrorCode]
